import React from 'react';
import { createSafeHtml } from '../../../../utils/htmlSanitizer';
import i18n from 'i18next';

/**
 * Translates validation error messages by extracting field labels and translating both
 * the field name and the validation message pattern.
 */
const translateErrorMessage = (msg: string): string => {
    const t = i18n.t.bind(i18n);

    // Pattern: "{fieldLabel} is required" or "{fieldLabel} is required."
    const requiredMatch = msg.match(/^(.+?)\s+is required\.?$/);
    if (requiredMatch) {
        const fieldLabel = requiredMatch[1];
        const translatedLabel = t(fieldLabel, { defaultValue: fieldLabel });
        return t('validation_is_required', { field: translatedLabel, defaultValue: `${translatedLabel} is required` });
    }

    // Pattern: "{fieldLabel} must be a number"
    const numberMatch = msg.match(/^(.+?)\s+must be a number$/);
    if (numberMatch) {
        const fieldLabel = numberMatch[1];
        const translatedLabel = t(fieldLabel, { defaultValue: fieldLabel });
        return t('validation_must_be_number', { field: translatedLabel, defaultValue: `${translatedLabel} must be a number` });
    }

    // Pattern: "{fieldLabel} has an ambiguous decimal separator"
    const ambiguousMatch = msg.match(/^(.+?)\s+has an ambiguous decimal separator$/);
    if (ambiguousMatch) {
        const fieldLabel = ambiguousMatch[1];
        const translatedLabel = t(fieldLabel, { defaultValue: fieldLabel });
        return t('validation_ambiguous_decimal_separator', { field: translatedLabel, defaultValue: `${translatedLabel} has an ambiguous decimal separator` });
    }

    // Pattern: "{fieldLabel} must be at least {min}{unit}"
    const minMatch = msg.match(/^(.+?)\s+must be at least\s+(.+)$/);
    if (minMatch) {
        const fieldLabel = minMatch[1];
        const minValue = minMatch[2];
        const translatedLabel = t(fieldLabel, { defaultValue: fieldLabel });
        return t('validation_must_be_at_least', { field: translatedLabel, min: minValue, defaultValue: `${translatedLabel} must be at least ${minValue}` });
    }

    // Pattern: "{fieldLabel} must be at most {max}{unit}"
    const maxMatch = msg.match(/^(.+?)\s+must be at most\s+(.+)$/);
    if (maxMatch) {
        const fieldLabel = maxMatch[1];
        const maxValue = maxMatch[2];
        const translatedLabel = t(fieldLabel, { defaultValue: fieldLabel });
        return t('validation_must_be_at_most', { field: translatedLabel, max: maxValue, defaultValue: `${translatedLabel} must be at most ${maxValue}` });
    }

    // For other messages, return as-is
    return msg;
};

/**
 * Utility function to render error messages with HTML content
 * Now uses sanitized HTML to prevent XSS attacks
 * Translates field labels and validation messages
 */
export const renderErrorMessage = (msg: string): React.ReactElement => (
    <div className="error formik-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }} dangerouslySetInnerHTML={createSafeHtml(translateErrorMessage(msg))} />
);
